import {
  EOptionSymbolSide,
  ESymbolCategory,
  ESymbolStatus,
  ISymbol,
  ISymbolDetail,
  marketAPIController
} from '@aqx-core/common/api/market';
import {
  ISymbolTopicLivePrices,
  SYM_SUB_KEY_UITRADE_ANALYSIS_QUOTES,
  SYM_SUB_KEY_UITRADE_TRADING_STATUS,
  symbolTopicSubscription
} from '@aqx-core/common/socket/subscriptions';
import { tradingStatusTopicSubscription } from '@aqx-core/common/socket/subscriptions/trading-status';
import storage, { EStorageKey } from '@aqx-core/common/storage';

import { companyViewController } from '../../company/controller';
import { searchViewController } from '../../search/controller';
import { tradeFormViewController } from '../form/controller';
import { EUIBuySellType } from '../form/types';
import { equityChartViewController } from './equity-chart/controller';
import { tradeAnalysisService } from './index';
import { optionService } from './option';
import { optionViewController } from './option/controller';
import { EUIOptionSide } from './option/types';
import { ISetSymbolDetailForOptionViewParams, ISetSymbolDetailParams, ISymbolDetailData } from './types';

class TradeAnalysisViewController {
  private symbolDetail?: Partial<ISymbolDetail>;
  private currentSymbolSubscriptionKey = new Set<string>();
  private setSymbolDetailCurrentRequestId = 0;
  private editingChartBarDataEnabled = false;

  private get editingSymbolDetail() {
    return tradeAnalysisService.context.editingSymbolDetail;
  }

  public getSymbolDetail = () => this.symbolDetail;

  public getEditingSymbolDetail = () => tradeAnalysisService.context.editingSymbolDetail;

  // eslint-disable-next-line complexity
  public setSymbolDetail = async ({
    symbolId,
    symbol,
    symbolType,
    buySellType,
    disableChartReload = false
  }: ISetSymbolDetailParams) => {
    const isSymbolAlreadySelected =
      symbolId === this.symbolDetail?.symbol ||
      (symbol?.symbol && this.symbolDetail?.symbol && symbol.symbol === this.symbolDetail.symbol);

    //* Increment request ID to prevent race conditions
    this.setSymbolDetailCurrentRequestId = this.setSymbolDetailCurrentRequestId + 1;
    const requestId = this.setSymbolDetailCurrentRequestId;

    let shouldLoadLineDrawing = false;
    if (symbolId !== tradeAnalysisService.context.symbolDetailId) {
      tradeFormViewController.setBuySellType(buySellType || EUIBuySellType.BUY);
    }

    this.unsubscribeSymbolTopic();
    this.unsubscribeSymbolTradingStatusTopic();
    /** Set symbol detail Id first */
    tradeAnalysisService.setSymbolDetailId(symbolId);

    if (symbol) {
      // Optimistically set partial symbol detail for faster UI response
      this.symbolDetail = symbol;
      tradeAnalysisService.setSymbolDetail(symbol);

      // Check if symbol data has ISymbol interface since `symbolGroup` is not a field in IDOMSymbolDetail
      const hasSymbolInterface = symbol.hasOwnProperty('symbolGroup');

      /**
       * Only execute equityChartViewController.setSymbol if
       * (1) if symbol has ISymbol interface (since ISymbol is the only acceptable struct)
       * (2) disableChartReload is false (i.e. enable chart reload)
       *
       * equityChartViewController.setSymbol will be run again after symbol detail is fully queried
       */
      if (!disableChartReload && hasSymbolInterface) {
        equityChartViewController.setSymbol(symbol as ISymbol);
      }

      /**
       * Only execute handleSetSymbolDetailForOptionView if symbol has ISymbol interface (since ISymbol is the only acceptable struct)
       *
       * handleSetSymbolDetailForOptionView will be run again after symbol detail is fully queried
       */
      if (hasSymbolInterface) {
        this.handleSetSymbolDetailForOptionView({ symbol: symbol as ISymbol, shouldFetch: false });
      }
    }

    /** If option , switch to option tab */
    if (!symbolType && !symbol?.type && !symbolId) {
      tradeAnalysisService.setTradingTab(undefined);
    } else if (symbolType === ESymbolCategory.OPTION || symbol?.type === ESymbolCategory.OPTION) {
      tradeAnalysisService.setTradingTab(ESymbolCategory.OPTION);
    } else if (symbolType === ESymbolCategory.FUTURES || symbol?.type === ESymbolCategory.FUTURES) {
      tradeAnalysisService.setTradingTab(ESymbolCategory.FUTURES);
      shouldLoadLineDrawing = true;
    } else {
      tradeAnalysisService.setTradingTab(ESymbolCategory.EQUITIES);
      shouldLoadLineDrawing = true;
    }

    if (!isSymbolAlreadySelected) {
      tradeAnalysisService.setSymbolDetailLoading(true);
    }

    await marketAPIController.getSymbolDetail(
      { symbol: symbolId },
      {
        onSuccess: async (symbolDetail) => {
          if (requestId !== this.setSymbolDetailCurrentRequestId || !symbolDetail) {
            // Required for tracking any errors when switching symbols on Trade page
            // eslint-disable-next-line no-console
            console.warn(
              `Symbol selection race condition detected: requestId ${requestId} !== current ${this.setSymbolDetailCurrentRequestId}`
            );
            return;
          }

          const chartSelectedSymbol = equityChartViewController.getSelectedSymbol();
          const isChartAlreadyLoaded = chartSelectedSymbol?.symbol === symbolDetail.symbol;
          if (!disableChartReload) {
            if (isChartAlreadyLoaded && shouldLoadLineDrawing) {
              //* Chart was already loaded by the optimistic call, only fetch line drawing
              await equityChartViewController.getLineDrawingUserPreference(symbolDetail.symbol);
              equityChartViewController.updateSelectedSymbol(symbolDetail);
            } else if (!isChartAlreadyLoaded) {
              await equityChartViewController.setSymbol(symbolDetail, shouldLoadLineDrawing);
            }
          }

          this.symbolDetail = symbolDetail;

          tradeAnalysisService.setSymbolDetail(symbolDetail);

          if (!companyViewController.isPC()) {
            storage.setItem(EStorageKey.LAST_SELECTED_SYMBOL, symbolDetail.symbol);
          }

          //* Only subscribe if this is still the latest request
          if (requestId === this.setSymbolDetailCurrentRequestId) {
            this.subscribeSymbolTopic();
            this.subscribeSymbolTradingStatusTopic();
            this.handleSetSymbolDetailForOptionView({ symbolDetail: symbolDetail });
          }

          const { type, tradableSecurityTypes } = symbolDetail;
          this.handleSetTradingTabFromSymbolDetail(type, tradableSecurityTypes);
        },
        onError: () => {},
        onFinally: () => {
          if (requestId === this.setSymbolDetailCurrentRequestId) {
            tradeAnalysisService.setSymbolDetailLoading(false);
          }
        }
      }
    );
  };

  public refetchSymbolDetail = async () => {
    const { symbol } = this.symbolDetail || {};
    if (symbol) {
      await marketAPIController.getSymbolDetail(
        {
          symbol
        },
        {
          onSuccess: async (symbolDetail) => {
            if (symbolDetail) {
              this.symbolDetail = symbolDetail;
              tradeAnalysisService.setSymbolDetail(this.symbolDetail);
            }
          },
          onError: () => {
            tradeAnalysisService.setSymbolDetailLoading(false);
          }
        }
      );
    }
  };

  public setTradingTab = (tab: ESymbolCategory) => {
    tradeAnalysisService.setTradingTab(tab);
  };

  public subscribeSymbolTopic = () => {
    const { symbolDetail } = this;

    if (symbolDetail && symbolDetail.symbol) {
      if (!this.currentSymbolSubscriptionKey.has(symbolDetail.symbol)) {
        symbolTopicSubscription.subscribe(
          symbolDetail.symbol,
          SYM_SUB_KEY_UITRADE_ANALYSIS_QUOTES,
          this.updateSymbolPrice,
          equityChartViewController.massageBarData,
          this.updateMarketStatus
        );
        this.currentSymbolSubscriptionKey.add(symbolDetail.symbol);
      }
    }
  };

  public unsubscribeSymbolTopic = () => {
    const { currentSymbolSubscriptionKey } = this;

    currentSymbolSubscriptionKey.forEach((symbol) => {
      if (symbol) {
        symbolTopicSubscription.unsubscribe(
          symbol,
          SYM_SUB_KEY_UITRADE_ANALYSIS_QUOTES,
          this.updateSymbolPrice,
          equityChartViewController.massageBarData,
          this.updateMarketStatus
        );
      }
    });
    currentSymbolSubscriptionKey.clear();
  };

  public subscribeSymbolTradingStatusTopic = () => {
    const { symbolDetail } = this;

    if (symbolDetail && symbolDetail.symbol) {
      tradingStatusTopicSubscription.subscribe(
        symbolDetail.symbol,
        SYM_SUB_KEY_UITRADE_TRADING_STATUS,
        this.refetchSymbolDetail
      );
    }
  };

  public unsubscribeSymbolTradingStatusTopic = () => {
    const { symbolDetail } = this;

    if (symbolDetail && symbolDetail?.symbol) {
      tradingStatusTopicSubscription.unsubscribe(
        symbolDetail.symbol,
        SYM_SUB_KEY_UITRADE_TRADING_STATUS,
        this.refetchSymbolDetail
      );
    }
  };

  public updateSymbolPrice = (id: string, livePrices: ISymbolTopicLivePrices) => {
    const { symbolDetail } = this;

    if (symbolDetail && symbolDetail.symbol === id) {
      const updatedSymbolDetail: Partial<ISymbolDetail> = this.getSymbolDetailWithUpdatedPrice(
        symbolDetail,
        livePrices
      );

      this.symbolDetail = updatedSymbolDetail;
      tradeAnalysisService.setSymbolDetail(updatedSymbolDetail);
    }
  };

  public updateMarketStatus = (id: string, isOpen: boolean) => {
    const { symbolDetail } = this;

    if (symbolDetail && symbolDetail.symbol === id) {
      const updatedSymbolDetail: Partial<ISymbolDetail> = {
        ...symbolDetail,
        status: isOpen ? ESymbolStatus.TRADING : ESymbolStatus.CLOSED
      };

      this.symbolDetail = updatedSymbolDetail;
      tradeAnalysisService.setSymbolDetail(updatedSymbolDetail);
    }
  };

  public optimisticUpdateSelectedSymbolWatch = (symbolId: string, isWatch: boolean) => {
    const { symbolDetail } = this;

    if (symbolDetail && symbolDetail.symbol === symbolId) {
      this.symbolDetail = {
        ...symbolDetail,
        watch: isWatch
      };

      tradeAnalysisService.setSymbolDetail(this.symbolDetail);
    }

    searchViewController.updateFavouriteSymbolList(symbolId, isWatch);
  };

  public initEditView = (symbolId: string, options?: { withChartBarData?: boolean }) => {
    if (!symbolId) {
      return;
    }

    this.editingChartBarDataEnabled = options?.withChartBarData ?? false;

    if (symbolId === this.symbolDetail?.symbol) {
      tradeAnalysisService.setEditingSymbolDetail(this.symbolDetail);
      this.subscribeEditingSymbolTopic();
    } else {
      this.fetchEditingSymbolDetail(symbolId);
    }
  };

  public resetEditView = () => {
    this.unsubscribeEditingSymbolTopic();
    this.editingChartBarDataEnabled = false;
    tradeAnalysisService.setEditingSymbolDetail(undefined);
  };

  public clearSymbolIdAndDetails = () => {
    tradeAnalysisService.setSymbolDetailId(undefined);
    tradeAnalysisService.setSymbolDetail(undefined);
  };

  private fetchEditingSymbolDetail = async (symbolId: string) => {
    tradeAnalysisService.setEditingSymbolDetailLoading(true);
    await marketAPIController.getSymbolDetail(
      {
        symbol: symbolId
      },
      {
        onSuccess: async (symbolDetail) => {
          if (symbolDetail) {
            tradeAnalysisService.setEditingSymbolDetail(symbolDetail);
            this.subscribeEditingSymbolTopic();
          }
        },
        onError: () => {},
        onFinally: () => {
          tradeAnalysisService.setEditingSymbolDetailLoading(false);
        }
      }
    );
  };

  private updateEditingSymbolPrice = (id: string, livePrices: ISymbolTopicLivePrices) => {
    const { editingSymbolDetail } = this;

    if (editingSymbolDetail && editingSymbolDetail.symbol === id) {
      const updatedSymbolDetail: Partial<ISymbolDetailData> = this.getSymbolDetailWithUpdatedPrice(
        editingSymbolDetail,
        livePrices
      );

      tradeAnalysisService.setEditingSymbolDetail(updatedSymbolDetail);
    }
  };

  private subscribeEditingSymbolTopic = () => {
    const { editingSymbolDetail } = this;

    if (editingSymbolDetail && editingSymbolDetail.symbol) {
      symbolTopicSubscription.subscribe(
        editingSymbolDetail.symbol,
        SYM_SUB_KEY_UITRADE_TRADING_STATUS,
        this.updateEditingSymbolPrice,
        this.editingChartBarDataEnabled ? equityChartViewController.massageBarData : undefined
      );
    }
  };

  private unsubscribeEditingSymbolTopic = () => {
    const { editingSymbolDetail } = this;

    if (editingSymbolDetail && editingSymbolDetail?.symbol) {
      symbolTopicSubscription.unsubscribe(
        editingSymbolDetail.symbol,
        SYM_SUB_KEY_UITRADE_TRADING_STATUS,
        this.updateEditingSymbolPrice,
        this.editingChartBarDataEnabled ? equityChartViewController.massageBarData : undefined
      );
    }
  };

  private handleSetSymbolDetailForOptionView = ({
    symbol,
    symbolDetail,
    shouldFetch
  }: ISetSymbolDetailForOptionViewParams) => {
    const { side, symbol: symbolId } = { ...symbol, ...symbolDetail };

    const { optionSide } = optionService.context;
    if ((side === EOptionSymbolSide.PUT || side === EOptionSymbolSide.CALL) && optionSide !== EUIOptionSide[side]) {
      optionViewController.onSelectOptionSide(EUIOptionSide.ALL);
    }
    const { underlyingSymbols } = symbolDetail || {};
    optionViewController.setUnderlyingSymbol(
      underlyingSymbols && underlyingSymbols.length > 0 ? underlyingSymbols?.[0] : symbolId,
      { symbolDetail, symbol },
      shouldFetch
    );
  };

  private handleSetTradingTabFromSymbolDetail = (type: ESymbolCategory, tradableSecurityTypes: ESymbolCategory[]) => {
    if (!type && tradableSecurityTypes?.length === 0) {
      tradeAnalysisService.setTradingTab(undefined);
    } else if (type === ESymbolCategory.OPTION) {
      tradeAnalysisService.setTradingTab(ESymbolCategory.OPTION);
    } else if (type === ESymbolCategory.FUTURES) {
      tradeAnalysisService.setTradingTab(ESymbolCategory.FUTURES);
    } else {
      tradeAnalysisService.setTradingTab(ESymbolCategory.EQUITIES);
    }
  };

  private getSymbolDetailWithUpdatedPrice = (
    symbolDetail: Partial<ISymbolDetail>,
    livePrices: ISymbolTopicLivePrices
  ) => {
    return {
      ...symbolDetail,
      low: livePrices.liveLow,
      high: livePrices.liveHigh,
      ask: livePrices.liveBuy,
      bid: livePrices.liveSell,
      lastPrice: livePrices.liveLastPrice,
      spread: livePrices.liveSpread
    };
  };

  public getCurrentSymbolDetail = (): Partial<ISymbolDetail> | undefined => {
    return tradeAnalysisService.context.symbolDetail;
  };
}

export const tradeAnalysisViewController = new TradeAnalysisViewController();
